import {
  allomancyCards,
  characterTypes,
  feruchemyCards,
  radiantOrderCards,
  surgeCards,
} from './data/investitureData';
import {
  challenges,
  complications,
  getAbilityTags,
  relationships,
  settings,
  stakes,
} from './data/scenarioData';
import { groundedPremises } from './data/groundedPremises';
import { scenarioFrames } from './data/scenarioFrames';
import {
  buildAllomanticLoadout,
  buildFeruchemicalLoadout,
  buildRadiantLoadout,
} from './data/characterLoadouts';
import {
  buildFeruchemistPortrait,
  buildMistbornPortrait,
  buildRadiantPortrait,
} from './data/characterPortraits';

function hashSeed(value) {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function createRng(seed) {
  let state = hashSeed(seed);
  return () => {
    state += 0x6d2b79f5;
    let result = state;
    result = Math.imul(result ^ (result >>> 15), result | 1);
    result ^= result + Math.imul(result ^ (result >>> 7), result | 61);
    return ((result ^ (result >>> 14)) >>> 0) / 4294967296;
  };
}

function pick(items, rng) {
  if (!items.length) return null;
  return items[Math.floor(rng() * items.length)];
}

function unique(values) {
  return [...new Set(values)];
}

function enabledFrom(cards, enabledIds) {
  return cards.filter((card) => enabledIds.has(card.id));
}

function getOrderSurgeNames(order) {
  return order.subtitle.split(' + ');
}

function getCompatibleOrders(pools) {
  const enabledSurges = new Set(pools.surges.map((surge) => surge.name));
  return pools.orders.filter((order) =>
    getOrderSurgeNames(order).some((surgeName) => enabledSurges.has(surgeName))
  );
}

function typeCanGenerate(type, pools) {
  switch (type.id) {
    case 'type-misting':
    case 'type-mistborn':
      return pools.allomancy.length > 0;
    case 'type-ferring':
    case 'type-full-feruchemist':
      return pools.feruchemy.length > 0;
    case 'type-twinborn':
      return pools.allomancy.length > 0 && pools.feruchemy.length > 0;
    case 'type-knight-radiant':
      return getCompatibleOrders(pools).length > 0;
    default:
      return false;
  }
}

function buildActor(type, pools, rng, reserveRng) {
  if (type.id === 'type-misting') {
    const power = pick(pools.allomancy, rng);
    const loadout = buildAllomanticLoadout(power, { rng: reserveRng });
    return {
      typeId: type.id,
      typeName: type.name,
      world: type.world,
      displayName: `${power.name} Misting (${power.subtitle})`,
      powerNames: [power.name],
      abilityLine: power.summary,
      abilityTags: unique([...getAbilityTags(power), 'investiture']),
      sourceCardIds: [type.id, power.id],
      ...loadout,
    };
  }

  if (type.id === 'type-mistborn') {
    const featuredPower = pick(pools.allomancy, rng);
    const portrait = buildMistbornPortrait(Math.floor(rng() * 3));
    const loadout = buildAllomanticLoadout(featuredPower, {
      versatile: true,
      availablePowers: pools.allomancy,
      rng: reserveRng,
    });
    return {
      typeId: type.id,
      typeName: type.name,
      world: type.world,
      displayName: 'Mistborn',
      powerNames: [`Featured: ${featuredPower.name}`],
      abilityLine: `You can burn the full suite of metals; this scenario foregrounds ${featuredPower.name}, which ${featuredPower.summary.toLowerCase()}`,
      abilityTags: unique([...getAbilityTags(featuredPower), 'investiture', 'versatile']),
      sourceCardIds: [type.id, ...pools.allomancy.map((power) => power.id)],
      portrait,
      ...loadout,
    };
  }

  if (type.id === 'type-ferring') {
    const power = pick(pools.feruchemy, rng);
    const portrait = buildFeruchemistPortrait(Math.floor(rng() * 2), { includeSazed: false });
    const loadout = buildFeruchemicalLoadout(power, { rng: reserveRng });
    return {
      typeId: type.id,
      typeName: type.name,
      world: type.world,
      displayName: `${power.name} Ferring (${power.subtitle})`,
      powerNames: [power.name],
      abilityLine: power.summary,
      abilityTags: unique([...getAbilityTags(power), 'investiture']),
      sourceCardIds: [type.id, power.id],
      portrait,
      ...loadout,
    };
  }

  if (type.id === 'type-full-feruchemist') {
    const featuredPower = pick(pools.feruchemy, rng);
    const portrait = buildFeruchemistPortrait(Math.floor(rng() * 3));
    const loadout = buildFeruchemicalLoadout(featuredPower, {
      versatile: true,
      availablePowers: pools.feruchemy,
      rng: reserveRng,
    });
    return {
      typeId: type.id,
      typeName: type.name,
      world: type.world,
      displayName: 'Full Feruchemist',
      powerNames: [`Featured: ${featuredPower.name}`],
      abilityLine: `You can use the full suite of metalminds; this scenario foregrounds ${featuredPower.name}, which ${featuredPower.summary.toLowerCase()}`,
      abilityTags: unique([...getAbilityTags(featuredPower), 'investiture', 'versatile']),
      sourceCardIds: [type.id, ...pools.feruchemy.map((power) => power.id)],
      portrait,
      ...loadout,
    };
  }

  if (type.id === 'type-twinborn') {
    const allomancy = pick(pools.allomancy, rng);
    const feruchemy = pick(pools.feruchemy, rng);
    const allomanticLoadout = buildAllomanticLoadout(allomancy, { rng: reserveRng });
    const feruchemicalLoadout = buildFeruchemicalLoadout(feruchemy, { rng: reserveRng });
    return {
      typeId: type.id,
      typeName: type.name,
      world: type.world,
      displayName: `Twinborn: ${allomancy.subtitle} / ${feruchemy.subtitle}`,
      powerNames: [`${allomancy.name} Allomancy`, `${feruchemy.name} Feruchemy`],
      abilityLine: `${allomancy.summary} You can also use ${feruchemy.name} to ${feruchemy.summary.toLowerCase()}`,
      abilityTags: unique([
        ...getAbilityTags(allomancy),
        ...getAbilityTags(feruchemy),
        'investiture',
        'teamwork',
      ]),
      sourceCardIds: [type.id, allomancy.id, feruchemy.id],
      equipment: [...allomanticLoadout.equipment, ...feruchemicalLoadout.equipment],
      reserves: [...allomanticLoadout.reserves, ...feruchemicalLoadout.reserves],
      loadoutLine: `${allomanticLoadout.loadoutLine} ${feruchemicalLoadout.loadoutLine}`,
    };
  }

  const compatibleOrders = getCompatibleOrders(pools);
  const order = pick(compatibleOrders, rng);
  const orderSurges = new Set(getOrderSurgeNames(order));
  const orderSurgeCards = surgeCards.filter((surge) => orderSurges.has(surge.name));
  const featuredSurge = pick(
    pools.surges.filter((surge) => orderSurges.has(surge.name)),
    rng
  );
  const singularOrder = order.name.endsWith('s') ? order.name.slice(0, -1) : order.name;
  const ideal = pick([2, 3, 3, 4], rng);
  const loadout = buildRadiantLoadout(order, ideal, { rng: reserveRng });
  const portrait = buildRadiantPortrait(order, ideal);

  return {
    typeId: type.id,
    typeName: type.name,
    world: type.world,
    displayName: singularOrder,
    powerNames: [order.name, `Featured: ${featuredSurge.name}`],
    abilityLine: `${order.name} have access to ${order.subtitle}; this scenario foregrounds ${featuredSurge.name}, which ${featuredSurge.summary.toLowerCase()}`,
    abilityTags: unique([
      ...getAbilityTags(featuredSurge),
      'investiture',
      'leadership',
      'teamwork',
    ]),
    sourceCardIds: [type.id, order.id, ...orderSurgeCards.map((surge) => surge.id)],
    portrait,
    ...loadout,
  };
}

const settingRoles = {
  scadrial: [
    'You are a railway inspector whose coworkers know you as careful, practical, and entirely ordinary.',
    'You are a constable responsible for people who would fear you if they knew the truth about your powers.',
    'You are a factory foreman with a crew that trusts your judgment more than any distant authority.',
    'You are a traveling courier supporting a family that believes your work is mundane and safe.',
  ],
  roshar: [
    'You are a caravan quartermaster responsible for workers, families, and supplies that cannot be replaced.',
    'You are a junior officer whose soldiers know your reputation but not the full extent of your bond.',
    'You are a field surgeon expected to preserve life before pursuing any military objective.',
    'You are an envoy whose visible use of power could alter a fragile political relationship.',
  ],
};

function addCharacterContexts(cast, setting, storySource, rng) {
  const roles = storySource?.roles || settingRoles[setting.world];
  return cast.map((actor, index) => ({
    ...actor,
    contextLine: roles[index] || pick(roles, rng),
  }));
}

const challengeSettingTags = {
  'failing-structure': ['urban', 'underground', 'industrial', 'tower', 'height'],
  'vanishing-target': ['urban', 'crowd', 'travel'],
  'missing-vulnerable-person': ['crowd', 'community', 'isolated', 'urban', 'travel'],
  'hidden-sabotage': ['industrial', 'machinery', 'metal', 'magical', 'resource'],
  'crowd-panic': ['crowd', 'urban', 'political', 'enclosed'],
  'hostage-standoff': ['law', 'political', 'frontier', 'enclosed', 'community'],
  'invested-overload': ['machinery', 'magical', 'resource', 'ancient'],
  'convoy-ambush': ['travel', 'wilderness', 'frontier', 'exposure'],
  'scarce-resource': ['survival', 'resource', 'isolated', 'travel', 'wilderness'],
  'false-accusation': ['social', 'political', 'community', 'crowd'],
  'public-duel': ['social', 'political', 'crowd', 'military'],
  'trapped-expedition': ['underground', 'travel', 'wilderness', 'exposure'],
  'spreading-disaster': ['fire', 'water', 'storm', 'structure', 'industrial'],
  'secret-infiltration': ['social', 'political', 'urban', 'enclosed'],
  'rival-rescuers': ['community', 'crowd', 'travel', 'isolated'],
  'realmatic-breach': ['magical', 'realmatic', 'ancient', 'travel'],
  'compromised-leader': ['political', 'military', 'community'],
  'prisoner-with-truth': ['law', 'community', 'isolated', 'political', 'mystery'],
};

function scoreChallenge(challenge, cast, tone, setting = null) {
  const abilityTags = new Set(cast.flatMap((actor) => actor.abilityTags));
  const relevance = challenge.preferredTags.filter((tag) => abilityTags.has(tag));
  const settingRelevance = setting
    ? (challengeSettingTags[challenge.id] || []).filter((tag) => setting.tags.includes(tag))
    : [];
  const toneScore = tone === 'balanced' || challenge.tones.includes(tone) ? 2 : 0;
  return {
    challenge,
    relevance,
    settingRelevance,
    score: 1 + relevance.length * 4 + settingRelevance.length * 3 + toneScore,
  };
}

function pickRelevantChallenge(cast, tone, setting, rng) {
  const scored = challenges.map((challenge) => scoreChallenge(challenge, cast, tone, setting));
  const abilityMatches = scored.filter((entry) => entry.relevance.length > 0);
  const settingMatches = scored.filter((entry) => entry.settingRelevance.length > 0);
  const matchedOnBoth = abilityMatches.filter((entry) => entry.settingRelevance.length > 0);
  const contextual = matchedOnBoth.length
    ? matchedOnBoth
    : abilityMatches.length
      ? abilityMatches
      : settingMatches.length
        ? settingMatches
        : scored;
  const bestScore = Math.max(...contextual.map((entry) => entry.score));
  const finalists = contextual.filter((entry) => entry.score >= Math.max(3, bestScore - 4));
  const total = finalists.reduce((sum, entry) => sum + entry.score, 0);
  let cursor = rng() * total;

  for (const entry of finalists) {
    cursor -= entry.score;
    if (cursor <= 0) return entry;
  }

  return finalists[finalists.length - 1];
}

function pickComplication(tone, rng) {
  const preferred = complications.filter(
    (complication) => tone === 'balanced' || complication.tones.includes(tone)
  );
  return pick(preferred.length ? preferred : complications, rng);
}

function pickGroundedPremise(tone, rng) {
  const preferred = groundedPremises.filter(
    (premise) => tone === 'balanced' || premise.challenge.tones.includes(tone)
  );
  return pick(preferred.length ? preferred : groundedPremises, rng);
}

function frameChallenge(frame) {
  return {
    id: `frame-${frame.id}`,
    frameId: frame.id,
    title: frame.title,
    preferredTags: frame.abilityTags,
    tones: frame.tones,
    problem: frame.incident,
    optionA: frame.optionA,
    optionB: frame.optionB,
  };
}

function pickRelevantFrame(world, cast, tone, rng) {
  const abilityTags = new Set(cast.flatMap((actor) => actor.abilityTags));
  const candidates = scenarioFrames.filter(
    (candidate) => candidate.settingId.startsWith(`${world}-`)
      && (tone === 'balanced' || candidate.tones.includes(tone))
  );
  const available = candidates.length
    ? candidates
    : scenarioFrames.filter((candidate) => candidate.settingId.startsWith(`${world}-`));
  const scored = available.map((candidate) => ({
    frame: candidate,
    relevance: candidate.abilityTags.filter((tag) => abilityTags.has(tag)),
  }));
  const bestScore = Math.max(...scored.map((entry) => entry.relevance.length));
  const finalists = scored.filter((entry) => entry.relevance.length >= Math.max(1, bestScore - 1));
  return pick(finalists.length ? finalists : scored, rng).frame;
}

function pickFramePressure(frame, previousPressure, rng) {
  const alternatives = frame.pressures.filter((pressure) => pressure !== previousPressure);
  return pick(alternatives.length ? alternatives : frame.pressures, rng);
}

function buildPools(enabledIds) {
  return {
    types: enabledFrom(characterTypes, enabledIds).filter((type) => !type.disabled),
    allomancy: enabledFrom(allomancyCards, enabledIds),
    feruchemy: enabledFrom(feruchemyCards, enabledIds),
    orders: enabledFrom(radiantOrderCards, enabledIds),
    surges: enabledFrom(surgeCards, enabledIds),
  };
}

function validTypesForWorld(pools, world) {
  return pools.types.filter((type) => type.world === world && typeCanGenerate(type, pools));
}

function castHasDisplayName(cast, displayName) {
  return cast.some((actor) => actor.displayName === displayName);
}

function buildCast({ pools, world, crossover, mode, rng, reserveRng }) {
  const candidates = pools.types.filter(
    (type) => (crossover || type.world === world) && typeCanGenerate(type, pools)
  );

  if (!candidates.length) {
    throw new Error(
      'The active pool has no complete character path. Enable a character type and the powers or Orders it needs.'
    );
  }

  const count = mode === 'pair' ? 2 : 1;
  const cast = [];

  for (let index = 0; index < count; index += 1) {
    let actor = buildActor(pick(candidates, rng), pools, rng, reserveRng);
    let attempts = 0;
    while (castHasDisplayName(cast, actor.displayName) && attempts < 6) {
      actor = buildActor(pick(candidates, rng), pools, rng, reserveRng);
      attempts += 1;
    }
    cast.push(actor);
  }

  return cast;
}

function buildNarrative(scenario) {
  const castIntro =
    scenario.mode === 'solo'
      ? `${scenario.cast[0].contextLine} You are also ${scenario.cast[0].displayName}. ${scenario.cast[0].abilityLine}`
      : `You are ${scenario.cast[0].displayName} and ${scenario.cast[1].displayName}. ${scenario.relationship}`;

  return {
    castIntro,
    situation: scenario.frame
      ? `${scenario.setting.atmosphere} ${scenario.frame.incident}`
      : `${scenario.setting.atmosphere} ${scenario.challenge.problem}`,
    pressure: scenario.frame
      ? `${scenario.frame.duty} ${scenario.framePressure} ${scenario.stakes}`
      : scenario.premise
      ? `${scenario.premisePressure} ${scenario.premise.stakes}`
      : `${scenario.complication.text} ${scenario.stakes}`,
    question: `Do you ${scenario.challenge.optionA}, or ${scenario.challenge.optionB}? What do you do?`,
  };
}

export function createScenarioSeed() {
  const time = Date.now().toString(36).slice(-5);
  const random = Math.floor(Math.random() * 1679616)
    .toString(36)
    .padStart(4, '0');
  return `${time}-${random}`.toUpperCase();
}

export function generateScenario({
  enabledIds,
  mode = 'solo',
  tone = 'balanced',
  scenarioKind = 'canon',
  crossover = false,
  seed = createScenarioSeed(),
  previous = null,
  preserve = [],
}) {
  const normalizedEnabledIds = enabledIds instanceof Set ? enabledIds : new Set(enabledIds);
  const pools = buildPools(normalizedEnabledIds);
  const rng = createRng(seed);
  const reserveRng = createRng(`${seed}-RESERVES`);
  const preserved = new Set(preserve);

  const worldsWithCharacters = ['scadrial', 'roshar'].filter(
    (world) => validTypesForWorld(pools, world).length > 0
  );

  if (!worldsWithCharacters.length) {
    throw new Error(
      'The active pool cannot form a character. Re-enable a character type and at least one compatible power path.'
    );
  }

  let characterWorld;
  if (!crossover && preserved.has('cast') && previous) characterWorld = previous.cast[0].world;
  else characterWorld = pick(crossover ? ['scadrial', 'roshar'] : worldsWithCharacters, rng);

  const premise = scenarioKind === 'earth'
    ? preserved.has('setting') && previous
      ? previous.premise
      : pickGroundedPremise(tone, rng)
    : null;

  const baseCast =
    preserved.has('cast') && previous
      ? previous.cast
      : buildCast({ pools, world: characterWorld, crossover, mode, rng, reserveRng });

  const selectedFrame = previous && (preserved.has('setting') || preserved.has('challenge'))
    ? previous.frame
    : premise
      ? null
      : pickRelevantFrame(characterWorld, baseCast, tone, rng);

  const setting = preserved.has('setting') && previous
    ? previous.setting
    : premise
      || settings.find((candidate) => candidate.id === selectedFrame?.settingId)
      || pick(settings.filter((candidate) => candidate.world === characterWorld), rng);
  const cast = addCharacterContexts(baseCast, setting, premise || selectedFrame, rng);

  const activeFrame = selectedFrame || null;

  const challengeResult =
    preserved.has('challenge') && previous
      ? scoreChallenge(previous.challenge, cast, tone, setting)
      : premise
        ? scoreChallenge(premise.challenge, cast, tone, setting)
        : activeFrame
          ? scoreChallenge(frameChallenge(activeFrame), cast, tone, setting)
        : pickRelevantChallenge(cast, tone, setting, rng);

  const framePressure = activeFrame
    ? preserved.has('complication') && previous
      ? previous.framePressure
      : pickFramePressure(activeFrame, previous?.framePressure, rng)
    : null;
  const premisePressure = premise
    ? preserved.has('complication') && previous
      ? previous.premisePressure
      : pickFramePressure(
          { pressures: premise.pressures || [premise.pressure] },
          previous?.premisePressure,
          rng
        )
    : null;

  const scenario = {
    seed,
    mode,
    tone,
    scenarioKind,
    crossover,
    world: setting.world,
    characterWorld,
    premise,
    premisePressure,
    frame: activeFrame,
    framePressure,
    setting,
    cast,
    challenge: challengeResult.challenge,
    relevanceTags: challengeResult.relevance,
    complication:
      activeFrame
        ? { id: `frame-pressure-${activeFrame.pressures.indexOf(framePressure)}`, text: framePressure }
        : premise
          ? {
              id: `premise-pressure-${(premise.pressures || [premise.pressure]).indexOf(premisePressure)}`,
              text: premisePressure,
            }
        : preserved.has('complication') && previous
        ? previous.complication
        : pickComplication(tone, rng),
    stakes: activeFrame
      ? preserved.has('stakes') && previous
        ? previous.stakes
        : activeFrame.stakes
      : premise
      ? premise.stakes
      : preserved.has('stakes') && previous
        ? previous.stakes
        : pick(stakes, rng),
    relationship:
      mode === 'pair'
        ? preserved.has('relationship') && previous
          ? previous.relationship
          : pick(relationships, rng)
        : null,
  };

  return {
    ...scenario,
    title: scenario.premise
      ? scenario.premise.title
      : scenario.frame
        ? scenario.frame.title
      : `${scenario.challenge.title} at ${scenario.setting.name}`,
    narrative: buildNarrative(scenario),
  };
}

export function formatScenarioForSharing(scenario) {
  const cast = scenario.cast
    .map((actor) => {
      const reserves = actor.reserves
        .map((reserve) => `${reserve.label}: ${reserve.amount} (${reserve.detail})`)
        .join('; ');
      const equipment = actor.equipment.map((item) => item.name).join(', ') || 'No oath-granted equipment';
      return `${actor.displayName}\n${actor.contextLine}\n${actor.abilityLine}\nLoadout: ${actor.loadoutLine}\nEquipment: ${equipment}\nReserves: ${reserves}`;
    })
    .join('\n\n');

  return [
    scenario.title,
    '',
    cast,
    '',
    scenario.narrative.situation,
    scenario.narrative.pressure,
    '',
    scenario.narrative.question,
    '',
    `Seed: ${scenario.seed}`,
    'Generated by Investiture Randomizer',
  ].join('\n');
}
